import { createReducer, on } from '@ngrx/store';

import { PlanType, QueryResult, RecordsResult } from '@mode-switch/query';
import {
  BinSizeTypes,
  ConditionalFormattingTypes,
  DataProfilingTypes,
  FlamingoResponse,
  FlamingoTypes,
  RMap,
} from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS, immRemoveKey } from '@mode/shared/util-js';
import { FormulaOperations } from '@mode/shared/util-switch';
import { FlamingoActions } from '@mode/switch-views/contract';

export const planFeatureKey = 'plan';

export interface State {
  ids: ReadonlyArray<string>;
  exports: RMap<string | Blob>;
  requests: RMap<FlamingoTypes.ModeSelectPlan>;
  results: RMap<FlamingoTypes.ModeSelectResult>;
  columnsByRef: RMap<FlamingoTypes.ResultColumns>;
  states: RMap<FlamingoTypes.PlanStatus>;
  errors: RMap<FlamingoResponse.FlamingoErrorResponse>;
  dataProfiling: RMap<DataProfilingTypes.DataProfilingMap>;
  binSizeToFieldMap: RMap<BinSizeTypes.BinSizeToFieldMap>;
  conditionalFormatting: RMap<ConditionalFormattingTypes.ConditionalFormattingMap>;
}

export const initialState: State = {
  ids: [],
  exports: {},
  requests: {},
  results: {},
  states: {},
  columnsByRef: {},
  errors: {},
  dataProfiling: {},
  binSizeToFieldMap: {},
  conditionalFormatting: {},
};

export const idsReducer = createReducer(
  initialState.ids,
  on(FlamingoActions.executePlan, FlamingoActions.executePagePlan, (state, action) =>
    state.indexOf(action.plan.id) <= 0 ? state.concat(action.plan.id) : state
  )
);

export const requestsReducer = createReducer(
  initialState.requests,
  on(FlamingoActions.executePlan, FlamingoActions.executePagePlan, (state, action) => ({
    ...state,
    [action.plan.id]: action.plan,
  }))
);

export const columnsReducer = createReducer(
  initialState.columnsByRef,
  on(FlamingoActions.planFailed, (state, action) => immRemoveKey(state, action.refId)),
  on(FlamingoActions.planSuccess, (state, action) => {
    if (action.request.type === PlanType.Records) {
      const { request, result } = action;
      const recordsResult = RecordsResult.recordsInline(request, result);
      if (recordsResult != null) {
        return {
          ...state,
          [action.refId]: recordsResult.columns,
        };
      }
    }

    return state;
  })
);

export const dataProfilingReducer = createReducer(
  initialState.dataProfiling,
  on(FlamingoActions.planFailed, (state, action) => immRemoveKey(state, action.refId)),
  on(FlamingoActions.planSuccess, (state, action) => {
    if (action.request.type === PlanType.PivotBasic && action.refId.includes(FLAMINGO_RESERVED_REFS.DataProfiling)) {
      if (action.result != null) {
        const resultsToColMap: DataProfilingTypes.DataProfilingMap = {};
        const timestamp = new Date().getTime();
        action.result.results.forEach((result) => {
          if (result.type === QueryResult.Type.Inline) {
            const first = result.items[0];
            if (typeof first?.['name'] === 'string') {
              resultsToColMap[first['name']] = { ...first, timestamp };
            }
          }
        });
        const chartToken = action.refId.split('#')[1];
        return {
          ...state,
          [chartToken]: resultsToColMap,
        };
      }
    }

    return state;
  })
);

export const binSizeReducer = createReducer(
  initialState.binSizeToFieldMap,
  on(FlamingoActions.planFailed, (state, action) => immRemoveKey(state, action.refId)),
  on(FlamingoActions.planSuccess, (state, action) => {
    if (action.request.type === PlanType.PivotBasic && action.refId.includes(FLAMINGO_RESERVED_REFS.BinSize)) {
      if (action.result != null) {
        const resultsToColMap: BinSizeTypes.BinSizeToFieldMap = new Map();
        action.result.results.forEach((result) => {
          if (result.type === QueryResult.Type.Inline) {
            const first = result.items[0] as BinSizeTypes.BinSizeMetaData | undefined;

            // Has a field name
            if (typeof first?.name === 'string') {
              resultsToColMap.set(first.name, { ...first, binSize: 1 /* default size? */ });

              // Has a finite bin size, round it off & use it
              if (Number.isFinite(first.binSize)) {
                const binSize = FormulaOperations.roundOffBinSize(first.binSize);
                resultsToColMap.set(first.name, { ...first, binSize });
              }
            }
          }
        });
        const chartToken = action.refId.split('#')[1];

        return {
          ...state,
          [chartToken]: resultsToColMap,
        };
      }
    }
    return state;
  })
);

export const conditionalFormattingReducer = createReducer(
  initialState.conditionalFormatting,
  on(FlamingoActions.planFailed, (state, action) => immRemoveKey(state, action.refId)),
  on(FlamingoActions.planSuccess, (state, action) => {
    if (
      action.request.type === PlanType.PivotBasic &&
      action.refId.includes(FLAMINGO_RESERVED_REFS.ConditionalFormatting)
    ) {
      if (action.result != null) {
        const resultsToColMap: ConditionalFormattingTypes.ConditionalFormattingMap = {};
        action.result.results.forEach((result) => {
          if (result.type === QueryResult.Type.Inline) {
            const first = result.items[0];
            if (typeof first?.['name'] === 'string') {
              resultsToColMap[first['name']] = {
                max: first['max'],
                min: first['min'],
              };
            }
          }
        });
        const chartToken = action.refId.split('#')[1];
        return {
          ...state,
          [chartToken]: {
            ...state?.[chartToken],
            ...resultsToColMap,
          },
        };
      }
    }

    return state;
  })
);

export const exportsReducer = createReducer(
  initialState.exports,
  on(FlamingoActions.exportPlanSuccess, (state, action) => ({
    ...immRemoveKey(state, action.prevPlanId),
    [action.request.id]: action.urlOrBlob,
  }))
);

export const resultsReducer = createReducer(
  initialState.results,
  on(FlamingoActions.planSuccess, FlamingoActions.pagingSuccess, (state, action) => ({
    ...immRemoveKey(state, action.prevPlanId),
    [action.request.id]: action.result,
  }))
);

export const statesReducer = createReducer(
  initialState.states,
  on(FlamingoActions.executePlan, FlamingoActions.executeExportPlan, (state, action) => ({
    ...state,
    [action.plan.id]: FlamingoTypes.PlanStatus.Loading,
  })),
  on(FlamingoActions.executePagePlan, (state, action) => ({
    ...state,
    [action.plan.id]: FlamingoTypes.PlanStatus.LoadingPage,
  })),
  on(
    FlamingoActions.planSuccess,
    FlamingoActions.exportPlanSuccess,
    FlamingoActions.pagingSuccess,
    (state, action) => ({
      ...state,
      [action.request.id]: FlamingoTypes.PlanStatus.Succeeded,
    })
  ),
  on(FlamingoActions.planFailed, FlamingoActions.exportPlanFailed, (state, action) => ({
    ...state,
    [action.planId]: FlamingoTypes.PlanStatus.Failed,
  }))
);

export const errorsReducer = createReducer(
  initialState.errors,
  on(FlamingoActions.planFailed, (state, action) => ({
    ...state,
    [action.planId]: action.response,
  }))
);

export const reducer = {
  ids: idsReducer,
  exports: exportsReducer,
  requests: requestsReducer,
  results: resultsReducer,
  states: statesReducer,
  columnsByRef: columnsReducer,
  errors: errorsReducer,
  dataProfiling: dataProfilingReducer,
  binSizeToFieldMap: binSizeReducer,
  conditionalFormatting: conditionalFormattingReducer,
};
